import { HTMLNodeTreeWalker } from "../graphs/HTMLNodeTreeWalker";
import { TreeWalker } from "../graphs/TreeWalker";
import { AttributeValue } from "./AttributeValue";
import { AutoDataPrefixer } from "./AutoDataPrefixer";
import { ClassFlag } from "./ClassFlag";
import { DOMNameSpaces } from "./DOMNameSpaces";
import { ElementAttribute } from "./ElementAttribute";
import { ElementType } from "./ElementType";
import { HTMLEditor } from "./HTMLEditor";
import { IDTag } from "./IDTag";
import { Query } from "./Query";

export class ElementSelector<E extends Element> implements Query
{
  private _type = ElementType.any;
  private _attributes:(ElementAttribute|AttributeValue|ClassFlag|IDTag)[] = [];

  constructor( type:ElementType<E>, ...atts:(ElementAttribute|AttributeValue|ClassFlag|IDTag)[] )
  {
    if ( type && type != null && type != undefined )
    { 
      this.type( type );
    } 
    
    this.attributes.apply( this, atts );
  }   


  
  type( type:ElementType<E> ):ElementSelector<E>
  {
    this._type = type;
    return this;
  }

  clearAttributes()
  {
    this._attributes = [];
    return this;
  }

  attributes( ...atts:(ElementAttribute|AttributeValue|ClassFlag)[] )
  {
    this._attributes = this._attributes.concat( atts );
    return this;
  }

  addAttributes( atts:(ElementAttribute|AttributeValue|ClassFlag)[] )
  {
    this._attributes = this._attributes.concat( atts );
    return this;
  }

  get selector():string
  {
    let value = this._type.selector;

    for( let i = 0; i < this._attributes.length; i++ )
    {
      value += this._attributes[ i ].selector;
    }

    return value;
  }



  query( t:Element|Document ):E
  {
    return t.querySelector( this.selector );
  }

  queryInDoc()
  {
    return this.query( document );
  }

  queryAll( t:Element|Document ):E[]
  {
    let elementList = t.querySelectorAll( this.selector );
    return HTMLEditor.nodeListToArray( elementList ) as E[];
  }  

  queryAllInDoc():E[]
  {
    return this.queryAll( document );
  }

  forAllIn( t:Element|Document, c:( e:E )=>void )
  {
    let elements = this.queryAll( t );
    elements.forEach( e => c( e ) );    
  }

  forAllInDoc( c:( e:E )=>void )
  {
    this.forAllIn( document, c ); 
  }


  selects( e:Element )
  {
    if ( ! this._type.is( e ) )
    {
      return false;
    }

    for ( let i = 0; i < this._attributes.length; i++ )
    {
      if ( ! this._attributes[ i ].in( e ) )
      {
        return false;
      }
    }

    return true;
  }

  getParent( child:Element ):E
  {
    let it = child.parentElement;

    while ( it )
    {
      if ( this.selects( it ) )
      {
        return it as any as E;
      }

      it = it.parentElement;
    }

    return null;
  }

  static queryValue( attribute:ElementAttribute, element:Element, sourceElement:Element = null )
  {
    if ( ! attribute.in( element ) )
    {
      return null;
    }

    let selector = ElementSelector.fromAttribute( element, attribute );


    sourceElement = sourceElement || element;
    return selector.query( sourceElement );
  }

  static queryValueAll( attribute:ElementAttribute, element:Element, sourceElement:Element = null )
  {
    let selector = ElementSelector.fromAttribute( element, attribute );

    sourceElement = sourceElement || element;
    return selector.queryAll( sourceElement );
  }

  static fromAttribute( element:Element, attribute:ElementAttribute,  )
  {
    return ElementSelector.parse( attribute.from( element ) );
  }

  static parse( cssString:string )
  {
    
    let useNameSpace = false;
    let nameSpace:string = null;

    if ( cssString.length > 0 && cssString[ 0 ] === ( "\"" ) )
    {
      let nameSpaceEnd = cssString.indexOf( "\"", 1 );

      if ( nameSpaceEnd !== -1 )
      {
        let nameSpaceValue = cssString.substring( 1, nameSpaceEnd );
        
        switch( nameSpaceValue )
        {
          case "SVG": nameSpace = DOMNameSpaces.SVG; break;
          case "XLink": nameSpace = DOMNameSpaces.XLink; break;
          case "": nameSpace = null; break;
          default: nameSpace = nameSpaceValue; break;
        }                
  
        cssString = cssString.substring( nameSpaceEnd + 1 );
        useNameSpace = true;
      }
      else
      {
        console.log( "Expected namespace definition, closer not found", cssString );
      }

    }

    let type = null;
    let classes = [];
    let id = null;
    let atts:{name:string, value:string}[] = [];
    let regex = HTMLEditor.cssCreationRegex;
    let result;

    while ( ( result = regex.exec( cssString ) ) !== null ) 
    {
      for ( let i = 1; i < result.length; i++ ) 
      {
        if ( result[ i ] ) 
        {
          let value = result[ 0 ];

          switch ( i ) 
          {
            case 1:
            {
              value = AutoDataPrefixer.apply(value);
              value = value.replace(/^\s*\[\s*/, "");
              value = value.replace(/\s*\]\s*$/, "");
              let attribute = value.split(/(?:\~|\||\^|\$|\*)?=/);
              let attributeName = attribute[0];
              attributeName = attributeName.replace(/^\s/, "");
              attributeName = attributeName.replace(/\s$/, "");
              let attributeValue = attribute[1];
              attributeValue = attributeValue.replace(/^('|")/, "");
              attributeValue = attributeValue.replace(/('|")$/, "");

              let attEntry = { name: attributeName, value: attributeValue }
              atts.push( attEntry );
            }
            break;

            case 2:
            {
              id = value.substring(1);
            }
            break;

            case 3:
            {
              classes.push(value.substring(1));
            }
            break;

            case 4:
            {
              let name = value;
              name = AutoDataPrefixer.apply(name);
              name = name.replace(/^\s*\[\s*/, "");
              name = name.replace(/\s*\]\s*$/, "");
              atts.push({ name: name, value: null });
            }
            break;

            case 5:
            {
              type = value;
            }
            break;
          }

          i = result.length;
        }
      }
    }

    let typeSelector = type ? new ElementType( type ) : null;
    let attributeSelectors:ElementAttribute[] = [];
    let attributeValueSelectors:AttributeValue[] = [];
    let classFlags:ClassFlag[] = [];
    
    if ( id )
    {
      attributeValueSelectors.push( new AttributeValue( ElementAttribute.id, id ) );
    }

    classes.forEach( c => classFlags.push( new ClassFlag( c ) ) );

    atts.forEach(
      ( att )=>
      {
        let ea = new ElementAttribute( att.name, false );

        if ( att.value )
        {
          attributeValueSelectors.push( new AttributeValue( ea, att.value ) );
        }
        else
        {
          attributeSelectors.push( ea );
        }
      }
    )

    let combinedAtts:(ElementAttribute|AttributeValue|ClassFlag)[]  = [];
    combinedAtts = combinedAtts.concat( attributeSelectors );
    combinedAtts = combinedAtts.concat( attributeValueSelectors );    
    combinedAtts = combinedAtts.concat( classFlags );
    
    let selector = new ElementSelector( typeSelector ).addAttributes( combinedAtts );

    return selector;
  
  }
}